import { Routes } from '@angular/router';
import { LayoutComponent } from './layout/layout.component';
import { PageNotFoundComponent } from './shared/components/page-not-found/page-not-found.component';
import { PostResolver } from './resolver/post.resolver';
import { DataResolver } from './resolver/data.resolver';
import { MilestoneResolver } from './resolver/milestones.resolver';
import { HustlesResolver } from './resolver/hustles.resolver';
import { QuizResolver } from './resolver/quizzes.resolver';
import { PodcastResolver } from './resolver/podcast.resolver';
import { PodcastDetailsResolver } from './resolver/podcastDetails.resolver';
import { EventDetailsResolver } from './resolver/eventDetails.resolver';
import { HomepageResolver } from './resolver/homepage.resolver';
import { MessagesComponent } from './features/chats/messages/messages.component';

const titleData = ' | Safety Knights';

export const routes: Routes = [

  // Homepage (marketing). The forum feed moved to /community.
  {
    path: '',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'Safety Knights | Workplace Safety Community',
      content: 'Join the Safety Knights community to explore workplace safety topics, industry news, and career insights for safety professionals.'
    },
    resolve: {
      homepage: HomepageResolver,
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/homepage/homepage.component').then((m) => m.HomepageComponent),
      },
    ],
  },

  // Community feed (was the '' route). Post URLs (/post/:post/:title) are unchanged.
  {
    path: 'community',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'Community Discussions' + titleData,
      content: 'Join the Safety Knights community to explore workplace safety topics, industry news, and career insights for safety professionals.'
    },
    resolve: {
      postData: DataResolver,
    },
    runGuardsAndResolvers: 'always',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/home/home.component').then((m) => m.HomeComponent),
      },
    ],
  },

  // Old feed-only paths. Query params (e.g. invite_ref) are preserved by the redirect.
  { path: 'home', redirectTo: 'community', pathMatch: 'full' },
  { path: 'feed', redirectTo: 'community', pathMatch: 'full' },
  { path: 'newsfeed', redirectTo: 'community', pathMatch: 'full' },
  { path: 'discussions', redirectTo: 'community', pathMatch: 'full' },

  // Details pages start
  {
    path: 'post/:post/:title',
    component: LayoutComponent,
    data: {
      type: 'post',
      layout: 'horizontal',
      title: 'Health and Safety Careers' + titleData,
      content: 'Join the Safety Knights community to explore workplace safety topics, industry news, and career insights for safety professionals.'
    },
    resolve: {
      postData: PostResolver,
    },
    runGuardsAndResolvers: 'always',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/post-details/post-details.component').then((m) => m.PostDetailsComponent),
      },
    ],
  },
  {
    path: 'announcement/:post/:title',
    component: LayoutComponent,
    data: {
      type: 'announcement',
      layout: 'horizontal',
      title: 'Health and Safety Careers' + titleData,
      content: 'Join the Safety Knights community to explore workplace safety topics, industry news, and career insights for safety professionals.'
    },
    resolve: {
      postData: PostResolver,
    },
    runGuardsAndResolvers: 'always',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/post-details/post-details.component').then((m) => m.PostDetailsComponent),
      },
    ],
  },
  {
    path: 'milestone/:post/:title',
    component: LayoutComponent,
    data: {
      type: 'milestone',
      layout: 'horizontal',
      title: 'Health and Safety Careers' + titleData,
      content: 'Join the Safety Knights community to explore workplace safety topics, industry news, and career insights for safety professionals.'
    },
    resolve: {
      postData: PostResolver,
    },
    runGuardsAndResolvers: 'always',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/post-details/post-details.component').then((m) => m.PostDetailsComponent),
      },
    ],
  },
  {
    path: 'quiz/:post/:title',
    component: LayoutComponent,
    data: {
      type: 'quiz',
      layout: 'horizontal',
      title: 'Health and Safety Careers' + titleData,
      content: 'Join the Safety Knights community to explore workplace safety topics, industry news, and career insights for safety professionals.'
    },
    resolve: {
      postData: PostResolver,
    },
    runGuardsAndResolvers: 'always',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/post-details/post-details.component').then((m) => m.PostDetailsComponent),
      },
    ],
  },
  {
    path: 'hustle/:post/:title',
    component: LayoutComponent,
    data: {
      type: 'hustle',
      layout: 'horizontal',
      title: 'Health and Safety Careers' + titleData,
      content: 'Join the Safety Knights community to explore workplace safety topics, industry news, and career insights for safety professionals.'
    },
    resolve: {
      postData: PostResolver,
    },
    runGuardsAndResolvers: 'always',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/post-details/post-details.component').then((m) => m.PostDetailsComponent),
      },
    ],
  },
  // End

  // Search page
  {
    path: 'search',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'Health and Safety Careers' + titleData,
      content: 'Join the Safety Knights community to explore workplace safety topics, industry news, and career insights for safety professionals.'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./shared/search-page/search-page.component').then((m) => m.SearchPageComponent),
      },
    ],
  },

  // Profile page
  {
    path: 'profile',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'Health and Safety Careers' + titleData,
      content: 'Join the Safety Knights community to explore workplace safety topics, industry news, and career insights for safety professionals.'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./shared/profile/profile-container/profile-container.component').then((m) => m.ProfileContainerComponent),
      },
    ],
  },

  // content page
  {
    path: 'content',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: `Safety Topics & Articles | Content for Safety Professionals ${titleData}`,
      content: 'Listen to a world of essential safety resources designed specifically for safety professionals! Tune into the Safety Knights podcast, where we cover a wide range of safety topics that are both engaging and informative. From interesting safety topics to practical workplace safety resources, our episodes are packed with valuable insights to support your health and safety career. The Safety Knights podcast has something for every safety professional.'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/content/content.component').then(m => m.ContentComponent),
      },

      {
        path: ':tab',
        loadComponent: () =>
          import('./modules/pages/content/content.component').then(m => m.ContentComponent),
      },

      {
        path: 'view',
        loadComponent: () =>
          import('./modules/pages/content/content.component').then(m => m.ContentComponent),
      },
    ],
  },


  // Event Details Page
  {
    path: 'event/:id/:title',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'Event' + titleData,
      content: 'Explore in-depth workplace safety podcast episodes with expert insights and real-world safety stories.',
      animation: 'DetailsPage'
    },
    resolve: {
      resolvedData: EventDetailsResolver,
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/events/event-details/event-details.component').then((m) => m.EventDetailsComponent),
      },
    ],
  },

  // Podcasts & Podcast Details
  {
    path: 'podcasts',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'Podcasts' + titleData,
      content:
        'Listen to our educational safety podcasts packed full of information for every safety professional. Listen to the Safety Knights podcast here!',
    },
    runGuardsAndResolvers: 'always',
    resolve: {
      podcastData: PodcastResolver,
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/podcasts/podcast/podcast.component').then(
            (m) => m.PodcastComponent
          ),
      },
      {
        path: ':id/:title',
        data: {
          layout: 'horizontal',
          title: 'Podcast Details' + titleData,
          content:
            'Explore in-depth workplace safety podcast episodes with expert insights and real-world safety stories.',
          animation: 'DetailsPage',
        },
        resolve: {
          resolvedData: PodcastDetailsResolver,
        },
        loadComponent: () =>
          import(
            './modules/pages/podcasts/podcast-details/podcast-details.component'
          ).then((m) => m.PodcastDetailsComponent),
      },
    ],
  },
  


  // Jobs page
  {
    path: 'jobs',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: "Explore Health and Safety Careers | Workplace Safety Hirings",
      content: "Discover rewarding health and safety careers. Find a workplace safety position that makes a difference. Start your path to a safer tomorrow today!"
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/jobs/jobs.component').then((m) => m.JobsComponent),
      },
    ],
  },

  // milestones pages
  {
    path: 'milestones',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'Milestones' + titleData,
      content: 'Discover side milestones and gig opportunities tailored for safety professionals to earn extra income.'
    },
    resolve: {
      postData: MilestoneResolver,
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/milestones/milestones.component').then((m) => m.MilestonesComponent),
      },
    ],
  },

  // quizzes page
  {
    path: 'quizzes',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'Quizzes' + titleData,
      content: 'Discover side hustles and gig opportunities tailored for safety professionals to earn extra income.'
    },
    resolve: {
      postData: QuizResolver,
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/quizzes/quizzes.component').then((m) => m.QuizzesComponent),
      },
    ],
  },

  //poeples-summit
  //Param here
  // {
  //   path: 'peoples_summit',
  //   component: LayoutComponent,
  //   data: {
  //     layout: 'horizontal',
  //     title: 'People\'s Summit' + titleData,
  //     content: 'Join safety professionals from around the world for five days of expert-led sessions, practical strategies, and free CEUs — 100% free to attend.'
  //   },
  //   children: [
  //     {
  //       path: '',
  //       loadComponent: () =>
  //         import('./modules/pages/peoples/peoples.component').then((m) => m.PeopleSafetySummitComponent),
  //     },
  //   ],
  // },

  // {
  //   path: 'pss26-live',
  //   component: LayoutComponent,
  //   data: {
  //     layout: 'horizontal',
  //     title: 'People\'s Summit' + titleData,
  //     content: 'Join safety professionals from around the world for five days of expert-led sessions, practical strategies, and free CEUs — 100% free to attend.'
  //   },
  //   children: [
  //     {
  //       path: '',
  //       loadComponent: () =>
  //         import('./modules/pages/summit-event/summitEvent.component').then((m) => m.SummitEventComponent),
  //     },
  //   ],
  // },

  {
    path: 'previousSummitStreams',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'Summit Streams' + titleData,
      content: 'Missed a session? We got you.'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/previousStreams/previous-streams.component').then((m) => m.PreviousStreamsComponent),
      },
    ],
  },

  {
    path: 'authorsChatbox',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'People\'s Summit' + titleData,
      content: 'Join safety professionals from around the world for five days of expert-led sessions, practical strategies, and free CEUs — 100% free to attend.'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/chatbox/chat.component').then((m) => m.SummitChatComponent),
      },
    ],
  },

  {
    path: 'peoples-safety-summit',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'People\'s Summit' + titleData,
      content: 'Join safety professionals from around the world for five days of expert-led sessions, practical strategies, and free CEUs — 100% free to attend.'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/peoples/peoples.component').then((m) => m.PeopleSafetySummitComponent),
      },
    ],
  },

  {
    path: 'schedule',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'People\'s Summit' + titleData,
      content: 'Join safety professionals from around the world for five days of expert-led sessions, practical strategies, and free CEUs — 100% free to attend.'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/schedule/summit-schedule.component').then((m) => m.SummitScheduleComponent),
      },
    ],
  },
  // Hustles page
  {
    path: 'hustles',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'Hustles' + titleData,
      content: 'Discover side hustles and gig opportunities tailored for safety professionals to earn extra income.'
    },
    resolve: {
      postData: HustlesResolver,
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/hustles/hustles.component').then((m) => m.HustlesComponent),
      },
    ],
  },

  // rewards page
  // {
  //   path: 'rewards',
  //   component: LayoutComponent,
  //   data: {
  //     layout: 'horizontal',
  //     title: 'Rewards' + titleData,
  //     content: 'Earn rewards and recognition for your contributions to the workplace safety community.'
  //   },
  //   children: [
  //     {
  //       path: '',
  //       loadComponent: () =>
  //         import('./modules/pages/reward/reward.component').then((m) => m.RewardComponent),
  //     },
  //   ],
  // },

  // referral page
  {
    path: 'referral',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'Referral' + titleData,
      content: 'Refer friends and colleagues to Safety Knights and earn exciting rewards for growing the community.'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/referral/referral.component').then((m) => m.ReferralComponent),
      },
    ],
  },

  // Confirm User page
  {
    path: 'confirmuser/:token',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'Confirm Your Account' + titleData,
      content: 'Complete your registration to start exploring all that Safety Knights has to offer.'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/user-confirmation/user-confirmation.component').then((m) => m.UserConfirmationComponent),
      },
    ],
  },

  // partners directory
  {
    path: 'partners',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'Preferred Partners | Safety Knights',
      content: 'Browse our curated directory of safety technology partners.'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/partners/partners.component').then((m) => m.PartnersComponent),
      },
    ],
  },

  // partner detail
  {
    path: 'partners/:slug',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'Partner Details | Safety Knights',
      content: 'Learn more about this Safety Knights preferred partner.'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/partner-detail/partner-detail.component').then((m) => m.PartnerDetailComponent),
      },
    ],
  },

  // sponsors page
  {
    path: 'sponsors',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'Register as a Sponsor' + titleData,
      content: 'Become a sponsor of Safety Knights and connect with professionals in the workplace safety industry.'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/sponsors/sponsors.component').then((m) => m.SponsorsComponent),
      },
    ],
  },

  // tutorial page
  // {
  //   path: 'tutorial',
  //   component: LayoutComponent,
  //   data: {
  //     layout: 'horizontal',
  //     title: 'Tutorial' + titleData,
  //     content: 'Access step-by-step tutorials on workplace safety best practices, tools, and techniques.'
  //   },
  //   children: [
  //     {
  //       path: '',
  //       loadComponent: () =>
  //         import('./modules/pages/tutorial/tutorial.component').then((m) => m.TutorialComponent),
  //     },
  //   ],
  // },

  // resources page
  // {
  //   path: 'resources',
  //   component: LayoutComponent,
  //   data: {
  //     layout: 'horizontal',
  //     title: 'Resources' + titleData,
  //     content: 'Explore a collection of workplace safety resources, tools, and guides to enhance your knowledge and compliance.'
  //   },
  //   children: [
  //     {
  //       path: '',
  //       loadComponent: () =>
  //         import('./modules/pages/resources/resources.component').then((m) => m.ResourcesComponent),
  //     },
  //   ],
  // },

  // Mission page
  {
    path: 'mission',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'Mission' + titleData,
      content: 'Learn about Safety Knights’ mission to create safer workplaces through education, community, and advocacy.'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/mission/mission.component').then((m) => m.MissionComponent),
      },
    ],
  },

  // guideline page
  {
    path: 'guidelines',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'Community Guidelines' + titleData,
      content: 'Community Guidelines'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/guideline/guideline.component').then((m) => m.GuidelineComponent),
      },
    ],
  },
  {
    path: 'terms',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'Terms and Conditions' + titleData,
      content: 'Terms and Conditions'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/terms/terms.component').then((m) => m.TermsComponent),
      },
    ],
  },
  {
    path: 'privacy',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'Privacy Policy' + titleData,
      content: 'Privacy Policy'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/privacy/privacy.component').then((m) => m.PrivacyComponent),
      },
    ],
  },

  // contact-us page
  {
    path: 'contact',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'Contact Us ' + titleData,
      content: 'Have a question or comment about Safety Knights? Let us know!'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/contact-us/contact-us.component').then((m) => m.ContactUsComponent),
      },
    ],
  },

  // Join us 
  {
    path: 'join-us',
    component: LayoutComponent,
    data: {
      layout: 'empty',
      title: 'Join Us ' + titleData,
      content: 'Join Us Safety Knights'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/join-us/join-us.component').then((m) => m.JoinUsComponent),
      },
    ],
  },

  // Live Videos
  {
    path: 'live',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'Live Video ' + titleData,
      content: 'Live Video Library'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/pages/videos/live-videos-library/live-videos-library.component').then((m) => m.LiveVideosLibraryComponent),
      },
    ],
  },

  // Messages

  {
    path: 'messages',
    component: LayoutComponent,
    data: {
      layout: 'horizontal',
      title: 'Messages ' + titleData,
      content: 'Message Center'
    },
    children: [
      {
        path: '',
        component: MessagesComponent,
        data: {
          layout: 'horizontal',
          title: 'Messages ' + titleData,
          content: 'Message Center'
        },
      },
      {
        path: ':userName',
        component: MessagesComponent,
      },
    ],
  },



  // Fallback Route

  // {
  //   path: '**',
  //   component: ComingSoonTwoComponent,
  //   data: {
  //     layout: 'horizontal',
  //     title: 'Coming Soon' + titleData,
  //     content: ''
  //   },
  // },

  {
    path: '**',
    component: PageNotFoundComponent,
    data: {
      layout: 'horizontal',
      title: '404 - Page Not Found' + titleData,
      content: 'Oops! The page you’re looking for doesn’t exist. Explore our workplace safety resources instead.'
    },
  },

];
